import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Form,
  Table,
  Row,
  Col,
  Dropdown,
  Button,
  Spinner,
  Modal,
  Alert,
  Badge,
} from "react-bootstrap";
import { io } from "socket.io-client";
import { FaMoneyBillWave } from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const socket = io(API_URL);

/* ──────────────────────────────────────────────────────────────
   Employee multi‐select dropdown
────────────────────────────────────────────────────────────── */
const EmployeeDropdownFilter = ({
  employees,
  selectedIds,
  setSelectedIds,
  onApply,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = employees.filter((emp) =>
    (emp.name || emp.Name || "")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const allSelected = selectedIds.length === employees.length;
  const toggleAll = () =>
    allSelected ? setSelectedIds([]) : setSelectedIds(employees.map((e) => e.id));
  const toggleOne = (id) =>
    selectedIds.includes(id)
      ? setSelectedIds(selectedIds.filter((i) => i !== id))
      : setSelectedIds([...selectedIds, id]);

  return (
    <Dropdown
      show={open}
      onToggle={() => setOpen(!open)}
      autoClose="outside"
      className="w-100"
    >
      <Dropdown.Toggle className="w-100 text-left" variant="outline-secondary">
        {selectedIds.length === 0 ? "All Employees" : `${selectedIds.length} selected`}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: "100%", padding: 12 }}>
        <Form.Control
          type="text"
          placeholder="Search employees…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2"
        />
        <Form.Check
          type="checkbox"
          label={`All (${employees.length})`}
          checked={allSelected}
          onChange={toggleAll}
        />
        <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, paddingRight: 4 }}>
          {filtered.map((emp) => (
            <Form.Check
              key={emp.id}
              type="checkbox"
              label={emp.name || emp.Name || ""}
              checked={selectedIds.includes(emp.id)}
              onChange={() => toggleOne(emp.id)}
            />
          ))}
        </div>
        <div className="d-flex justify-content-end mt-2">
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              setOpen(false);
              setSearch("");
              onApply && onApply();
            }}
          >
            Apply
          </Button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

/* ──────────────────────────────────────────────────────────────
   Cash Amount Modal
────────────────────────────────────────────────────────────── */
const CashAmountModal = ({ 
  show, 
  onHide, 
  selectedLeave, 
  onSave,
  processing 
}) => {
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (selectedLeave) {
      setAmount(selectedLeave.compensation_amount || "");
    }
  }, [selectedLeave]);

  const handleSave = () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      alert("Please enter a valid cash amount.");
      return;
    }
    onSave(parseFloat(amount));
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Set Cash Compensation Amount</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedLeave && (
          <>
            <div className="mb-3">
              <p><strong>Employee:</strong> {selectedLeave.employee_name}</p>
              <p><strong>Employee ID:</strong> {selectedLeave.employee_id}</p>
              <p><strong>Leave Date:</strong> {selectedLeave.leave_date}</p>
              <p><strong>Leave Day:</strong> {selectedLeave.leave_day}</p>
              <p><strong>Leave Type:</strong> {selectedLeave.leave_type}</p>
            </div>
            
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Cash Amount (₹)</strong>
              </Form.Label>
              <Form.Control
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                min="0"
                step="0.01"
                autoFocus
              />
              <Form.Text className="text-muted">
                Enter the compensation amount for this leave day.
              </Form.Text>
            </Form.Group>
            
            <Alert variant="info">
              <strong>Note:</strong> Setting the cash amount will mark this as compensated.
            </Alert>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={processing}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={processing || !amount}
        >
          {processing ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            'Save Amount'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/* ──────────────────────────────────────────────────────────────
   Main component
────────────────────────────────────────────────────────────── */
const EmployeeLeaves = () => {
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState("");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [showCashModal, setShowCashModal] = useState(false);
  const [selectedLeaveForCash, setSelectedLeaveForCash] = useState(null);
  const [processingCash, setProcessingCash] = useState(false);

  const sortByNameAscending = useCallback((data) =>
    data.sort((a, b) =>
      (a.name || a.Name || "").toLowerCase().localeCompare((b.name || b.Name || "").toLowerCase())
    ), []
  );

  const fetchEmployeesList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`);
      setEmployees(sortByNameAscending(data.map(u => ({ id: u.id, name: u.Name }))));
    } catch (e) {
      console.error(e);
    }
  }, [sortByNameAscending]);

  const fetchOfficeEmployees = useCallback(async (office) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/logincrd?office=${office}`);
      setEmployees(sortByNameAscending(data.map(u => ({ id: u.employee_id, name: u.Name }))));
    } catch (e) {
      console.error(e);
    }
  }, [sortByNameAscending]);

  const fetchLeaves = useCallback(async (office = "") => {
    setLoading(true);
    try {
      const url = office
        ? `${API_URL}/api/employeeleavesdate?office=${office}`
        : `${API_URL}/api/employeeleavesdate`;
      const { data } = await axios.get(url);
      
      // Add a compensation_amount field if not present
      const leavesWithComp = data.map(leave => ({
        ...leave,
        compensation_amount: leave.compensation_amount || 0,
        is_compensated: leave.is_compensated || false
      }));
      
      setLeaves(leavesWithComp);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  // ─── FETCH OFFICES ─────────────────────────────────
  const fetchOffices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/holidays/offices`);
      setOffices(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Save cash amount to leave record
  const saveCashAmount = async (amount) => {
    if (!selectedLeaveForCash) return;
    
    setProcessingCash(true);
    try {
      // Update the leave record with compensation amount
      const response = await axios.put(
        `${API_URL}/api/employeeleavesdate/${selectedLeaveForCash.id || selectedLeaveForCash.employee_id}`,
        {
          ...selectedLeaveForCash,
          compensation_amount: amount,
          is_compensated: true,
          compensation_date: new Date().toISOString().split('T')[0]
        }
      );

      if (response.status === 200) {
        // Update local state
        setLeaves(prev => 
          prev.map(leave => 
            leave.employee_id === selectedLeaveForCash.employee_id && 
            leave.leave_date === selectedLeaveForCash.leave_date
              ? { 
                  ...leave, 
                  compensation_amount: amount,
                  is_compensated: true,
                  compensation_date: new Date().toISOString().split('T')[0]
                }
              : leave
          )
        );
        
        setShowCashModal(false);
        setSelectedLeaveForCash(null);
        alert("Cash amount saved successfully!");
      }
    } catch (error) {
      console.error("Error saving cash amount:", error);
      alert("Failed to save cash amount.");
    } finally {
      setProcessingCash(false);
    }
  };

  // Open cash amount modal
  const openCashModal = (leave) => {
    setSelectedLeaveForCash(leave);
    setShowCashModal(true);
  };

  useEffect(() => {
    fetchOffices();
    fetchEmployeesList();
    fetchLeaves();

    socket.on("officesChanged", fetchOffices);
    socket.on("employeesListUpdated", () =>
      selectedOffice ? fetchOfficeEmployees(selectedOffice) : fetchEmployeesList()
    );
    socket.on("employeeLeavesDateChanged", () => fetchLeaves(selectedOffice));

    return () => {
      socket.off("officesChanged", fetchOffices);
      socket.off("employeesListUpdated");
      socket.off("employeeLeavesDateChanged");
      socket.disconnect();
    };
  }, [
    fetchOffices,
    fetchEmployeesList,
    fetchOfficeEmployees,
    fetchLeaves,
    selectedOffice,
  ]);

  const handleOfficeChange = async (e) => {
    const officeName = e.target.value;
    setSelectedOffice(officeName);
    setSelectedEmployeeIds([]);
    setSelectedLeaveTypes([]);

    if (officeName) {
      await fetchOfficeEmployees(officeName);
      await fetchLeaves(officeName);
    } else {
      await fetchEmployeesList();
      await fetchLeaves();
    }
  };

  const toggleLeaveType = (type) => {
    setSelectedLeaveTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const selectedNames = employees
    .filter(e => selectedEmployeeIds.includes(e.id))
    .map(e => e.name);

  const filteredLeaves = leaves.filter(l => {
    const byEmp = !selectedEmployeeIds.length || selectedNames.includes(l.employee_name);
    const byType = !selectedLeaveTypes.length || selectedLeaveTypes.includes(l.leave_type);
    return byEmp && byType;
  });

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedLeaves = useMemo(() => {
    if (!sortConfig.key) return filteredLeaves;

    return [...filteredLeaves].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // If sorting by date, convert string to Date object
      if (sortConfig.key === "leave_date") {
        const [aDay, aMonth, aYear] = aVal.split("-");
        const [bDay, bMonth, bYear] = bVal.split("-");
        aVal = new Date(+aYear, +aMonth - 1, +aDay);
        bVal = new Date(+bYear, +bMonth - 1, +bDay);
      }

      if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  }, [filteredLeaves, sortConfig]);

  const getArrow = (key) =>
    sortConfig.key === key
      ? (sortConfig.direction === "ascending" ? " ↑" : " ↓")
      : "";

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount || amount === 0 || amount === '0.00') return 'Not Set';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: "auto", backgroundColor: "#f5f7fa", }}>
      <h3 className="text-center mb-4">Employee Leaves</h3>
      <Form className="mb-2">
        <Row className="align-items-end">
          <Col xs={12} md={3} className="mb-2">
            <Form.Group controlId="officeSelect">
              <Form.Label><b>Office</b></Form.Label>
              <Form.Control as="select" value={selectedOffice} onChange={handleOfficeChange}>
                <option value="">All Offices</option>
                {offices.map(o => (
                  <option key={o.id} value={o.name}>{o.name}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col xs={12} md={3} className="mb-2">
            <Form.Group controlId="employeeFilter">
              <Form.Label><b>Employee</b></Form.Label>
              <EmployeeDropdownFilter
                employees={employees}
                selectedIds={selectedEmployeeIds}
                setSelectedIds={setSelectedEmployeeIds}
                onApply={() => { }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={3} className="mb-2">
            <Form.Group controlId="leaveTypeFilter">
              <Form.Label><b>Leave Type</b></Form.Label>
              <div className="mt-1">
                {["Planned", "Unplanned"].map((t) => (
                  <Form.Check
                    inline
                    key={t}
                    type="checkbox"
                    id={`leave-type-${t}`}
                    label={t}
                    checked={selectedLeaveTypes.includes(t)}
                    onChange={() => toggleLeaveType(t)}
                  />
                ))}
              </div>
            </Form.Group>
          </Col>
          <Col xs={12} md={3} className="mb-2">
            <div className="d-flex align-items-center h-100">
              <Button 
                variant="primary" 
                onClick={() => {
                  // Refresh data
                  fetchLeaves(selectedOffice);
                }}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" animation="border" /> : "Refresh"}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      {loading ? (
        <div className="text-center"><Spinner animation="border" /></div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Sr.No</th>
                <th onClick={() => requestSort("employee_id")} style={{ cursor: "pointer" }}>
                  Employee Id{getArrow("employee_id")}
                </th>
                <th onClick={() => requestSort("employee_name")} style={{ cursor: "pointer" }}>
                  Employee Name{getArrow("employee_name")}
                </th>
                <th onClick={() => requestSort("leave_date")} style={{ cursor: "pointer" }}>
                  Leave Date{getArrow("leave_date")}
                </th>
                <th onClick={() => requestSort("leave_day")} style={{ cursor: "pointer" }}>
                  Leave Day{getArrow("leave_day")}
                </th>
                <th onClick={() => requestSort("leave_type")} style={{ cursor: "pointer" }}>
                  Leave Type{getArrow("leave_type")}
                </th>
                <th>Compensation Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedLeaves.map((l, i) => (
                <tr key={`${l.employee_id}-${l.leave_date}-${i}`}>
                  <td>{i + 1}</td>
                  <td>{l.employee_id}</td>
                  <td>{l.employee_name}</td>
                  <td>{l.leave_date}</td>
                  <td>{l.leave_day}</td>
                  <td>{l.leave_type}</td>
                  <td>
                    {formatCurrency(l.compensation_amount)}
                  </td>
                  <td>
                    {l.is_compensated ? (
                      <Badge bg="success">Compensated</Badge>
                    ) : (
                      <Badge bg="warning">Pending</Badge>
                    )}
                  </td>
                  <td>
                    {!l.is_compensated && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => openCashModal(l)}
                        title="Set Cash Amount"
                      >
                        <FaMoneyBillWave /> Set Amount
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Cash Amount Modal */}
      <CashAmountModal
        show={showCashModal}
        onHide={() => {
          setShowCashModal(false);
          setSelectedLeaveForCash(null);
        }}
        selectedLeave={selectedLeaveForCash}
        onSave={saveCashAmount}
        processing={processingCash}
      />
    </div>
  );
};

export default EmployeeLeaves;